import React, { useEffect, useState, useMemo } from 'react';
import { TiDelete } from 'react-icons/ti';
import { instance } from '../../api/axiosInstance';

interface Badge {
  id: number; // 엠블럼 ID
  name: string;
  image: string;
  tag: string;
  description: string;
  status: string;
  isAcquired: boolean; // 사용자가 보유한 엠블럼 여부
}

export default function BadgePage() {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [userBadgeIds, setUserBadgeIds] = useState<number[]>([]); // 사용자가 보유한 엠블럼 ID 목록
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 로컬 스토리지에서 사용자 ID 가져오기
  const user = JSON.parse(localStorage.getItem('user') || '{}'); // 사용자 정보를 가져오고 파싱
  const userId = user.id; // 사용자 ID

  useEffect(() => {
    fetchBadges();
    if (userId) {
      fetchUserBadges();
    }
  }, [userId]);

  const fetchBadges = async () => {
    try {
      const response = await instance.get('/api/emblems');
      setBadges(response.data);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
      setError(error as Error);
    }
  };

  const fetchUserBadges = async () => {
    try {
      if (userId) {
        const response = await instance.get(`/api/users/${userId}/emblems`);
        // 사용자 엠블럼 ID 목록 설정
        setUserBadgeIds(response.data.map((userBadge: { emblemId: number }) => userBadge.emblemId));
      }
    } catch (error) {
      console.error('Failed to fetch user badges:', error);
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const totalBadges = badges.length;
  const acquiredBadges = useMemo(
      () => userBadgeIds.length, // 보유한 엠블럼 수
      [userBadgeIds]
  );

  // badges 상태를 업데이트하여 각 배지의 isAcquired 속성 설정
  const updatedBadges = badges.map(badge => ({
    ...badge,
    isAcquired: userBadgeIds.includes(badge.id), // 사용자 ID가 포함되어 있으면 true
  }));

  if (loading) {
    return <div>로딩 중...</div>;
  }

  if (error) {
    return (
        <section className="mt-[58px] max-w-[1220px] mx-auto w-full py-[50px] flex items-center justify-center">
          <div>엠블럼을 불러오지 못했습니다. 나중에 다시 시도해주세요.</div>
        </section>
    );
  }

  if (totalBadges === 0) {
    return (
        <div className="text-center mt-10">
          <h1 className="font-bold text-2xl">엠블럼</h1>
          <p className="text-base mt-2">아직 엠블럼이 없습니다.</p>
        </div>
    );
  }

  return (
      <section className="mt-[58px] max-w-[1220px] mx-auto w-full py-[50px] px-[80px] md:px-0 ml-[30px] md:mx-auto">
        <div>
          <h1 className="font-bold text-3xl text-center md:text-left">엠블럼</h1>
          <p className="text-base mt-2 font-bold text-[#b9b9b9] text-center md:text-left">
            {acquiredBadges}/{totalBadges}
          </p>
          <p className="text-sm mt-2 font-normal text-center md:text-left px-[20px] md:p-0">
            다양한 목표를 달성하여 엠블럼을 획득하세요. 엠블럼을 획득하면 다양한
            에셋부터 할인 혜택까지 다양한 이벤트도 진행하니 놓치지 마세요!
          </p>
        </div>
        <div className="flex flex-col md:flex-row mt-10">
          <div className="w-full grid grid-cols-3 sm:grid-cols-2 md:grid-cols-8 gap-4">
            {updatedBadges.map((badge) => (
                <div
                    key={badge.id}
                    className={`p-4 shadow-md bg-gray-100 sm:max-w-[80px] sm:max-h-[80px] md:max-w-[100px] md:max-h-[100px] rounded-md cursor-pointer ${
                        selectedBadge?.id === badge.id
                            ? 'outline outline-4 outline-MainColor'
                            : ''
                    }`}
                    onClick={() => setSelectedBadge(badge)}
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setSelectedBadge(badge);
                      }
                    }}
                >
                  <div className="flex justify-center items-center h-full">
                    <img
                        src={`${process.env.REACT_APP_API_BASE_URL}${badge.image}`}
                        alt={badge.name}
                        className={`sm:w-[60px] sm:h-[60px] md:w-[80px] md:h-[80px] object-cover object-center ${
                            !badge.isAcquired ? 'opacity-50 filter grayscale' : ''
                        }`}
                    />
                  </div>
                </div>
            ))}
          </div>

          <div className="w-full md:w-1/4 p-4 min-h-[410px] shadow-custom bg-gray-50 sticky top-16 rounded-md self-start mt-4 md:mt-0">
            {selectedBadge ? (
                <div>
                  <p className="text-MainTextColor font-normal">
                    {selectedBadge.tag}
                  </p>
                  <h2 className="text-2xl text-MainTextColor font-bold mt-6">
                    {selectedBadge.name}
                  </h2>
                  <p className="text-MainTextColor font-medium text-xs mt-1">
                    {selectedBadge.description}
                  </p>
                  <div className="flex items-center justify-center mt-4">
                    <div className="max-w-[225px] max-h-[210px]">
                      <img
                          src={`${process.env.REACT_APP_API_BASE_URL}${selectedBadge.image}`}
                          alt={selectedBadge.name}
                          className={`w-full h-auto object-cover object-center ${
                              !selectedBadge.isAcquired ? 'opacity-50 filter grayscale' : ''
                          }`} // 보유하지 않은 경우 필터 적용
                      />
                    </div>
                  </div>
                  <div className="absolute bottom-4">
                    <div className="flex items-center gap-1 leading-9">
                      {selectedBadge.isAcquired ? (
                          <>
                            <TiDelete className="text-[#28a745] text-lg" />
                            <p className="text-MainTextColor font-medium text-sm">
                              획득한 엠블럼입니다.
                            </p>
                          </>
                      ) : (
                          <>
                            <TiDelete className="text-[#fc1c49] text-lg" />
                            <p className="text-MainTextColor font-medium text-sm">
                              아직 획득하지 않았습니다.
                            </p>
                          </>
                      )}
                    </div>
                  </div>
                </div>
            ) : (
                <div className="flex justify-center max-h-[410px] items-center">
                  <p className="font-normal text-[#454545] text-base">
                    엠블럼을 선택하세요
                  </p>
                </div>
            )}
          </div>
        </div>
      </section>
  );
}